import { useEffect, useRef, useState } from "react";
import Vapi from "@vapi-ai/web";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Mail, Phone, MessageCircle, Mic, PhoneOff, Loader2 } from "lucide-react";

const CONTACTS = {
  email: "contact@callswiper.com",
  phone: "713-965-3136",
  whatsapp: "+1 713-965-3136",
  whatsappNumber: "17139653136", // no + or dashes for wa.me
};

const SUPPORT_PUBLIC_KEY = "a2d78cfa-ff04-4a6a-850d-1ec701bce16f";
const SUPPORT_ASSISTANT_ID = "dbbded53-c08e-48d4-ba89-d650bce3a8d4";

export function GetHelpDialog({ open, onOpenChange }) {
  const vapiRef = useRef(null);
  const [callStatus, setCallStatus] = useState("idle"); // idle | connecting | live

  useEffect(() => {
    if (!vapiRef.current) vapiRef.current = new Vapi(SUPPORT_PUBLIC_KEY);
    const v = vapiRef.current;
    const onStart = () => setCallStatus("live");
    const onEnd = () => setCallStatus("idle");
    const onError = () => setCallStatus("idle");
    v.on("call-start", onStart);
    v.on("call-end", onEnd);
    v.on("error", onError);
    return () => {
      try { v.off?.("call-start", onStart); v.off?.("call-end", onEnd); v.off?.("error", onError); } catch { /* noop */ }
    };
  }, []);

  // Never leave an orphan call running when the dialog closes
  useEffect(() => {
    if (!open && callStatus !== "idle") {
      try { vapiRef.current?.stop(); } catch { /* noop */ }
      setCallStatus("idle");
    }
  }, [open, callStatus]);

  const toggleCall = async () => {
    const v = vapiRef.current;
    if (!v) return;
    if (callStatus !== "idle") {
      try { v.stop(); } catch { /* noop */ }
      setCallStatus("idle");
      return;
    }
    setCallStatus("connecting");
    try {
      await v.start(SUPPORT_ASSISTANT_ID);
    } catch {
      setCallStatus("idle");
    }
  };

  const isLive = callStatus === "live";
  const isConnecting = callStatus === "connecting";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="get-help-dialog">
        <DialogHeader>
          <DialogTitle>Get help</DialogTitle>
          <DialogDescription>Our team is here to help. Reach out to us via any of these channels.</DialogDescription>
        </DialogHeader>

        <button
          type="button"
          onClick={toggleCall}
          data-testid="help-voice-call-btn"
          className={`relative w-full mt-2 flex items-center justify-center gap-2.5 rounded-xl px-4 py-3.5 font-semibold text-sm text-white shadow-lg transition-all duration-300 focus:outline-none focus:ring-4 overflow-hidden ${
            isLive
              ? "bg-rose-500 hover:bg-rose-600 focus:ring-rose-500/30"
              : isConnecting
              ? "bg-amber-500 focus:ring-amber-500/30"
              : "bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-400 focus:ring-blue-500/30"
          }`}
        >
          {isLive && <span className="absolute inset-0 bg-rose-400/30 animate-pulse" />}
          <span className="relative flex items-center gap-2.5">
            {isConnecting ? (
              <Loader2 className="w-4.5 h-4.5 w-5 h-5 animate-spin" />
            ) : isLive ? (
              <PhoneOff className="w-4.5 h-4.5 w-5 h-5" />
            ) : (
              <Mic className="w-4.5 h-4.5 w-5 h-5" />
            )}
            {isConnecting ? "Connecting…" : isLive ? "End call — you're live with support" : "Talk to our support agent"}
          </span>
        </button>
        {isLive && (
          <p className="text-xs text-center text-muted-foreground -mt-1 animate-pulse" data-testid="help-call-live-hint">
            Speak now — the support agent can hear you.
          </p>
        )}

        <div className="space-y-2.5 mt-2">
          <a
            href={`mailto:${CONTACTS.email}`}
            data-testid="help-email-link"
            className="flex items-center gap-3 rounded-lg border border-border bg-muted/30 hover:bg-primary/5 hover:border-primary/40 transition-colors p-3.5 group"
          >
            <span className="w-9 h-9 rounded-md bg-primary/10 border border-primary/25 flex items-center justify-center text-primary">
              <Mail className="w-4 h-4" />
            </span>
            <span className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Email</div>
              <div className="text-sm font-semibold group-hover:text-primary transition-colors">{CONTACTS.email}</div>
            </span>
          </a>

          <a
            href={`tel:+1${CONTACTS.whatsappNumber}`}
            data-testid="help-phone-link"
            className="flex items-center gap-3 rounded-lg border border-border bg-muted/30 hover:bg-primary/5 hover:border-primary/40 transition-colors p-3.5 group"
          >
            <span className="w-9 h-9 rounded-md bg-primary/10 border border-primary/25 flex items-center justify-center text-primary">
              <Phone className="w-4 h-4" />
            </span>
            <span className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Phone</div>
              <div className="text-sm font-semibold group-hover:text-primary transition-colors">{CONTACTS.phone}</div>
            </span>
          </a>

          <a
            href={`https://wa.me/${CONTACTS.whatsappNumber}`}
            target="_blank"
            rel="noreferrer noopener"
            data-testid="help-whatsapp-link"
            className="flex items-center gap-3 rounded-lg border border-border bg-muted/30 hover:bg-emerald-500/5 hover:border-emerald-500/40 transition-colors p-3.5 group"
          >
            <span className="w-9 h-9 rounded-md bg-emerald-500/10 border border-emerald-500/25 flex items-center justify-center text-emerald-500">
              <MessageCircle className="w-4 h-4" />
            </span>
            <span className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">WhatsApp</div>
              <div className="text-sm font-semibold group-hover:text-emerald-500 transition-colors">{CONTACTS.whatsapp}</div>
            </span>
          </a>
        </div>
      </DialogContent>
    </Dialog>
  );
}
